// ============================================
// FORGOT PASSWORD PAGE - WITH TURNSTILE + i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { Turnstile } from '@/components/Turnstile';
import { useTranslation, Trans } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

// i18n destekli schema factory
function createForgotPasswordSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().email(t('auth:validation.invalidEmail')),
  });
}

type ForgotPasswordFormData = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

// API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://api.crewatsea.com/api/v1';

function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const forgotPasswordSchema = useMemo(() => createForgotPasswordSchema(t), [t]);

  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordFormData) => {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, turnstileToken }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || t('forgotPassword.errorDefault'));
      }
      return response.json();
    },
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    setSubmittedEmail(data.email);
    forgotPasswordMutation.mutate(data);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Mavi overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.user')}</p>
        </div>

        {/* Form Kartı */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {isSuccess ? (
            // Başarı Durumu
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('forgotPassword.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                <Trans
                  i18nKey="auth:forgotPassword.successMessage"
                  values={{ email: submittedEmail }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t('forgotPassword.spamHint')}
              </p>
              <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            // Form Durumu
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('forgotPassword.title')}</h2>
              <p className="text-gray-600 text-sm mb-6">
                {t('forgotPassword.subtitle')}
              </p>

              {/* Hata Mesajı */}
              {forgotPasswordMutation.isError && (
                <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {forgotPasswordMutation.error instanceof Error
                      ? forgotPasswordMutation.error.message
                      : t('forgotPassword.errorDefault')}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* E-posta */}
                <div>
                  <label htmlFor="email" className="label">
                    {t('forgotPassword.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>

                {/* Turnstile */}
                <div className="flex justify-center">
                  <Turnstile onVerify={setTurnstileToken} />
                </div>

                {/* Gönder Butonu */}
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={forgotPasswordMutation.isPending || !turnstileToken}
                >
                  {forgotPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('forgotPassword.submitting')}
                    </>
                  ) : (
                    t('forgotPassword.submit')
                  )}
                </button>
              </form>

              {/* Giriş Sayfasına Dön */}
              <p className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-maritime-600 hover:text-maritime-700 font-medium inline-flex items-center">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
